import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, useMap, GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Mill, DataLayerId, Scenario, ScenarioStep, SpeciesOverlayVariant, SupplyShedDriveTime } from '../../lib/types';
import { MapZoomControl } from '../shared/MapZoomControl';
import { BasemapSwitchControl } from '../shared/BasemapSwitchControl';
import { BasemapZoomClamp } from '../shared/BasemapZoomClamp';
import { WaterMask } from '../shared/WaterMask';
import { MAP_CONFIG } from '../../lib/mapConfig';
import { BASEMAPS } from '../../lib/basemaps';
import { useBasemap } from '../../context/BasemapContext';
import { useMapReady } from '../../hooks/useMapReady';
import { useWhpOverlay } from '../../hooks/useWhpOverlay';
import { useSpeciesOverlay } from '../../hooks/useSpeciesOverlay';
import { useSdOverlay } from '../../hooks/useSdOverlay';
import { useFoOverlay } from '../../hooks/useFoOverlay';
import { useFoPrivateMaskOverlay } from '../../hooks/useFoPrivateMaskOverlay';
import { useCanopyHeightOverlay } from '../../hooks/useCanopyHeightOverlay';
import { useAgbOverlay } from '../../hooks/useAgbOverlay';
import { useAgbChangeOverlay } from '../../hooks/useAgbChangeOverlay';
import { useSoilCarbonOverlay } from '../../hooks/useSoilCarbonOverlay';
import { useImperiledSpeciesOverlay } from '../../hooks/useImperiledSpeciesOverlay';
import { useResiliencePriorityOverlay } from '../../hooks/useResiliencePriorityOverlay';
import {
  usePerCrownNativeWindow,
  hasPerCrownCoverage,
} from '../../hooks/usePerCrownNativeWindow';
import { WhpMapLegend } from './WhpMapLegend';
import { ImperiledSpeciesMapLegend } from './ImperiledSpeciesMapLegend';
import { ResiliencePriorityMapLegend } from './ResiliencePriorityMapLegend';
import { PerCrownNativeOverlay } from './PerCrownNativeOverlay';
import { SpeciesMapLegend } from './SpeciesMapLegend';
import { SpeciesOverlayToggle } from './SpeciesOverlayToggle';
import { SdMapLegend } from './SdMapLegend';
import { FoMapLegend } from './FoMapLegend';
import { CanopyHeightMapLegend } from './CanopyHeightMapLegend';
import { AgbMapLegend } from '../shared/AgbMapLegend';
import { DemoBadge } from '../shared/DemoBadge';
import { AgbTimeSlider } from './AgbTimeSlider';
import { AgbCrossfadeOverlay } from './AgbCrossfadeOverlay';
import { AgbChangeMapLegend } from './AgbChangeMapLegend';
import { SoilCarbonMapLegend } from './SoilCarbonMapLegend';
import { AuthTileLayer } from './AuthTileLayer';
import { SupplyShedClipPane } from './SupplyShedClipPane';
import { SpotlightMask } from './SpotlightMask';
import { DetailLayerDropdown } from './DetailLayerDropdown';
import { PngRasterOverlay } from './PngRasterOverlay';
import { MobileScenarioMapOverlay } from './MobileScenarioMapOverlay';

// Fix for default marker icon in React-Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/mill-marker.svg',
  iconUrl: '/mill-marker.svg',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

import type { GeoJSON as GeoJSONType } from '../../lib/types';

// Data layers that render a translucent raster overlay on the map. When one
// of these is active while a thematic basemap (AGB etc.) is showing, we
// swap the basemap inside the shed to Stadia satellite so the overlay's
// color ramp isn't tinted by the basemap's own color ramp underneath.
const LAYERS_WITH_RASTER_OVERLAY: ReadonlySet<DataLayerId> = new Set([
  'fire_risk',
  'species_diversity',
  'stand_density',
  'forest_ownership',
  'canopy_height',
  'aboveground_biomass',
  'soil_carbon',
  'imperiled_species',
]);

interface IsochroneState {
  boundary: GeoJSONType.Polygon | null;
  isLoading: boolean;
  error: string | null;
}

interface DetailMapViewProps {
  mill: Mill;
  // Primary layer — drives UI chrome (legends, sliders, overlay toggles).
  // In free exploration this is the user's dropdown pick; in scenario mode
  // it's the last-added layer of the current step.
  selectedLayer: DataLayerId | null;
  // Full set of layers that should paint raster overlays on the map. In
  // free exploration this equals `[selectedLayer]`; in scenario mode it's
  // the step's declared state, which can include multiple simultaneous
  // layers (e.g. AGB + Forest Ownership).
  activeLayers: DataLayerId[];
  selectedDriveTime: SupplyShedDriveTime;
  supplyShed: IsochroneState;
  onLayerChange: (layer: DataLayerId) => void;
  speciesOverlayVariant: SpeciesOverlayVariant;
  onSpeciesOverlayVariantChange: (variant: SpeciesOverlayVariant) => void;
  // Year of the AGB raster currently rendered. Swapping this value
  // re-resolves the S3 tile URL, which invalidates Leaflet's tile cache
  // and triggers a fresh fetch for the year's raster.
  agbYear: number;
  onAgbYearChange: (year: number) => void;
  // Depth profile for the soil-carbon raster. Swapping 30 ↔ 100 picks the
  // sibling Flux dataset (different URL, different colorscalerange) so
  // Leaflet re-fetches tiles for the new depth automatically.
  soilCarbonDepth: 30 | 100;
  // Optional scenario-driven camera target. When present, the map flies to
  // this point instead of the default shed-bounds fit.
  scenarioMapFocus?: { center: [number, number]; zoom: number } | null;
  // When true, paint the private-timberland mask above the categorical
  // Forest Ownership PNG so family + corporate stay vibrant and every other
  // class dims. Driven by `currentStep.overlayVariants.forest_ownership` —
  // off in free exploration.
  foPrivateFocus?: boolean;
  // When true, swap the AGB year-tile crossfade for the precomputed change
  // PNG (smoothed-endpoint diff). The year slider hides while this is on
  // because the change product replaces it. Driven by
  // `currentStep.overlayVariants.aboveground_biomass === 'change'` — false
  // in free exploration.
  agbChangeMode?: boolean;
  // Native-resolution per-crown view — when true, the per-tree-forest-
  // attributes layer paints individual crown polygons inside its 1 km
  // demo window AND the camera flies there once on the rising edge. On
  // the falling edge the camera flies back out: in scenario mode it
  // returns to the active step's mapFocus, in free mode it pulls back to
  // (entry zoom − 2) at the same center. The earlier "leave the camera
  // wherever they were" rule was unhelpful — exiting native view mid-
  // demo left the audience staring at upscaled tiles.
  nativeViewActive: boolean;
  onExitNativeView: () => void;
  // Scenario context — surfaced as a floating card on the mobile map so a
  // user in map mode (sheet collapsed) keeps the step narrative in view.
  // All four are nullable: free exploration leaves them null and the
  // overlay doesn't render. Desktop ignores them (overlay is `md:hidden`).
  scenarioStep: ScenarioStep | null;
  activeScenario: Scenario | null;
  scenarioStepIndex: number;
  scenarioTotalSteps: number;
  scenarioIsFirstStep: boolean;
  scenarioIsLastStep: boolean;
  onScenarioPrev: () => void;
  onScenarioNext: () => void;
  onOpenMobileSheet: () => void;
  /** Open the bottom sheet AND auto-scroll to the insight card. Used by
      the final step's "Open insight ↗" button so the user lands on the
      insight rather than the panel top. */
  onOpenMobileSheetToInsight: () => void;
}

// Component to handle map view updates — fits map to buffered boundary.
// Gated on `useMapReady` so flyToBounds runs against real container bounds
// rather than the 0×0 snapshot Leaflet sees during pre-layout init.
const MapViewController: React.FC<{
  mill: Mill;
  boundary: GeoJSONType.Polygon | null;
  scenarioMapFocus?: { center: [number, number]; zoom: number } | null;
  // Rising-edge target for the per-crown native-view fly. When this changes
  // from null → coords (button toggled on AND GeoJSON loaded), we fly once.
  // Ref tracking so subsequent panel re-renders with the same target don't
  // re-trigger the fly.
  nativeFlyTarget: { center: [number, number]; zoom: number } | null;
}> = ({ mill, boundary, scenarioMapFocus, nativeFlyTarget }) => {
  const map = useMap();
  const ready = useMapReady(map);
  const hasHadBoundary = useRef(false);

  // Single consolidated camera-fly effect with explicit priority: scenario
  // `mapFocus` > shed bounds > mill fallback. Previously the bounds-fit and
  // scenario-focus lived in separate effects; if a scenario step declared
  // no `mapFocus` (e.g. carbon_offense steps 1–3), the camera relied on the
  // earlier-mounted bounds-fit having fired in time. When the step
  // transitioned and bounds didn't change, neither effect re-ran and the
  // camera could be stuck at the initial mill setView (z=8) — too wide for
  // the user to see the shed framed. Combining the two gives every step
  // change a chance to reconsider the framing.
  useEffect(() => {
    if (!ready) return;
    if (scenarioMapFocus) {
      map.flyTo(scenarioMapFocus.center, scenarioMapFocus.zoom, { duration: 0.8 });
      return;
    }
    if (boundary) {
      hasHadBoundary.current = true;
      const bounds = L.geoJSON(boundary as any).getBounds();
      if (bounds.isValid()) {
        // Tighter framing on mobile — narrow viewports left way too much
        // slack at padding=30 / maxZoom=10, the shed read as a small blob.
        // 12 px padding + maxZoom 12 lets Leaflet pick the highest fitting
        // zoom up to z=12; for the actual shed extents (60- and 90-min
        // sheds), this lands at z=10 or 11 with the shed near-edge-to-edge.
        // Desktop still benefits — a touch closer than the prior 30/10.
        const isNarrow =
          typeof window !== 'undefined' && window.innerWidth < 768;
        map.flyToBounds(bounds, {
          padding: isNarrow ? [12, 12] : [24, 24],
          maxZoom: 12,
          duration: 0.8,
        });
        return;
      }
    }
    if (!hasHadBoundary.current) {
      map.setView([mill.latitude, mill.longitude], 8);
    }
  }, [ready, scenarioMapFocus, boundary, mill, map]);

  // Native-view rising/falling-edge fly. The effect runs whenever the
  // target identity changes; the `prevTargetRef` guard distinguishes
  // rising edges (null → coords) from falling edges (coords → null) from
  // no-op re-renders.
  //
  // Rising edge — two-stage cinematic: first land at the precompute's
  // `flyZoom` (wide shot — whole basin boundary + banner readable), then
  // auto-zoom in ~2 levels so individual crown polygons become resolvable.
  // Without the second beat, a 5 km² watershed at z=14 has trees that are
  // sub-pixel and the audience can't tell what they're being shown. The
  // stage-2 flyTo is gated through `setTimeout` so the user sees the wide
  // view long enough to read it; the cleanup function clears the timer on
  // unmount or when the user toggles off mid-fly so we don't fight their
  // navigation.
  //
  // Falling edge — single fly back out. In scenario mode (scenarioMapFocus
  // set) we return to the active step's framing; in free mode we pull
  // back to (entry zoom − 2) at the same center, a clearly-distinct wide
  // shot without going all the way to the shed-bounds fit.
  const prevTargetRef = useRef<{ center: [number, number]; zoom: number } | null>(null);
  useEffect(() => {
    if (!ready) return;
    const prev = prevTargetRef.current;
    prevTargetRef.current = nativeFlyTarget;

    // Falling edge: was active, now off. Fly back out.
    if (prev && !nativeFlyTarget) {
      if (scenarioMapFocus) {
        map.flyTo(scenarioMapFocus.center, scenarioMapFocus.zoom, { duration: 1.0 });
      } else {
        const exitZoom = Math.max(prev.zoom - 2, 8);
        map.flyTo(prev.center, exitZoom, { duration: 1.0 });
      }
      return;
    }

    // Rising edge: previously null/different, now non-null.
    if (!nativeFlyTarget) return;
    if (
      prev &&
      prev.center[0] === nativeFlyTarget.center[0] &&
      prev.center[1] === nativeFlyTarget.center[1] &&
      prev.zoom === nativeFlyTarget.zoom
    ) {
      return;
    }
    // Stage 1: wide context shot.
    map.flyTo(nativeFlyTarget.center, nativeFlyTarget.zoom, { duration: 1.0 });
    // Stage 2: zoom in to "see the trees" after a short pause. 2.5 s pause =
    // 1 s for stage 1 to settle + 1.5 s of "look at this" beat. Increment is
    // capped at 18 to honor any per-layer max-zoom clamp.
    const closeZoom = Math.min(nativeFlyTarget.zoom + 2, 18);
    const timeoutId = window.setTimeout(() => {
      map.flyTo(nativeFlyTarget.center, closeZoom, { duration: 1.5 });
    }, 2500);
    return () => window.clearTimeout(timeoutId);
  }, [ready, nativeFlyTarget, scenarioMapFocus, map]);

  return null;
};

export const DetailMapView: React.FC<DetailMapViewProps> = ({
  mill,
  selectedLayer,
  activeLayers,
  selectedDriveTime,
  supplyShed,
  onLayerChange,
  speciesOverlayVariant,
  onSpeciesOverlayVariantChange,
  agbYear,
  onAgbYearChange,
  soilCarbonDepth,
  scenarioMapFocus,
  foPrivateFocus = false,
  agbChangeMode = false,
  nativeViewActive,
  onExitNativeView,
  scenarioStep,
  activeScenario,
  scenarioStepIndex,
  scenarioTotalSteps,
  scenarioIsFirstStep,
  scenarioIsLastStep,
  onScenarioPrev,
  onScenarioNext,
  onOpenMobileSheet,
  onOpenMobileSheetToInsight,
}) => {
  const boundary = supplyShed.boundary;
  // React-Leaflet's GeoJSON ignores data prop updates — it only renders on mount.
  // Include coordinate count so the component remounts when the actual polygon
  // changes. Assumes a simple Polygon with a single outer ring; breaks silently
  // for MultiPolygon or polygons with holes.
  const boundaryKey = `supply-shed-${mill.id}-drive-${selectedDriveTime}-${boundary?.coordinates[0]?.length ?? 0}`;

  const millCode = (mill.metadata?.code as string | undefined) ?? null;
  const { basemap, openPicker } = useBasemap();
  const showSpotlight = basemap.kind === 'thematic' && boundary !== null;

  // Auth-failure surface for the Earthmover Flux soil-carbon overlay. The
  // AuthTileLayer fires onAuthError once per layer instance when the
  // server returns 401/403; we hold the latest status here so a banner can
  // surface above the map. Rebuilt cleanly when authHeader changes (the
  // layer itself rebuilds, instance state resets) — but we also clear it
  // on layer toggle so a re-enable starts fresh.
  const [soilCarbonAuthError, setSoilCarbonAuthError] = useState<number | null>(null);
  const handleSoilCarbonAuthError = useCallback((status: number) => {
    setSoilCarbonAuthError(status);
    // Devs need to know — ops surfaces the user-facing banner; this trail
    // ends up in the browser console for triage.
    console.error(`[AuthTileLayer] soil_carbon auth failed: HTTP ${status}. Check VITE_EARTHMOVER_TOKEN.`);
  }, []);
  useEffect(() => {
    if (!activeLayers.includes('soil_carbon')) {
      setSoilCarbonAuthError(null);
    }
  }, [activeLayers]);

  const whpOverlay = useWhpOverlay(millCode, selectedDriveTime, activeLayers.includes('fire_risk'));
  const speciesOverlay = useSpeciesOverlay(
    millCode,
    selectedDriveTime,
    speciesOverlayVariant,
    activeLayers.includes('species_diversity'),
  );
  const sdOverlay = useSdOverlay(millCode, selectedDriveTime, activeLayers.includes('stand_density'));
  const foOverlay = useFoOverlay(millCode, selectedDriveTime, activeLayers.includes('forest_ownership'));
  const foPrivateMaskOverlay = useFoPrivateMaskOverlay(
    millCode,
    selectedDriveTime,
    foPrivateFocus && activeLayers.includes('forest_ownership'),
  );
  const canopyHeight = useCanopyHeightOverlay(
    mill.state,
    activeLayers.includes('canopy_height'),
    boundary,
  );
  const agbOverlay = useAgbOverlay(agbYear);
  const agbChangeOverlay = useAgbChangeOverlay(
    millCode,
    selectedDriveTime,
    agbChangeMode && activeLayers.includes('aboveground_biomass'),
  );
  const soilCarbonOverlay = useSoilCarbonOverlay(
    soilCarbonDepth,
    activeLayers.includes('soil_carbon'),
  );
  const imperiledSpeciesOverlay = useImperiledSpeciesOverlay(
    millCode,
    selectedDriveTime,
    activeLayers.includes('imperiled_species'),
  );
  const resiliencePriorityOverlay = useResiliencePriorityOverlay(
    millCode,
    selectedDriveTime,
    activeLayers.includes('resilience_priority'),
  );
  // Native window — fetched as soon as the per-crown layer is active so the
  // GeoJSON is hot in cache by the time the user clicks "View individual
  // trees". `fetchWithCache` dedupes with PerCrownContent's parallel call.
  const perCrownNative = usePerCrownNativeWindow(
    millCode,
    selectedDriveTime,
    activeLayers.includes('tree_crown_segmentation') && hasPerCrownCoverage(millCode),
  );

  // Rising-edge target for MapViewController's fly effect. Only non-null
  // when the user has flipped the toggle on AND the GeoJSON resolves; the
  // controller fires `flyTo` exactly once each time this transitions from
  // null → coords.
  const nativeFlyTarget = useMemo(() => {
    if (!nativeViewActive || !perCrownNative.data) return null;
    return {
      center: perCrownNative.data.centerLatLng,
      zoom: perCrownNative.data.flyZoom,
    };
  }, [nativeViewActive, perCrownNative.data]);
  // Bubbled up by AgbCrossfadeOverlay whenever its pending tiles start/
  // finish loading. Displayed on AgbTimeSlider as a "Loading" / "Ready"
  // badge so the user knows whether they're looking at a fresh raster or
  // still the previous year's backdrop.
  const [isAgbOverlayLoading, setIsAgbOverlayLoading] = useState(false);
  // Stable callback so the crossfade effect doesn't see a fresh closure
  // on every DetailMapView render (which would cause redundant work).
  const handleAgbLoadingChange = useCallback((loading: boolean) => {
    setIsAgbOverlayLoading(loading);
  }, []);

  // Lat/lng bbox of the supply shed polygon. Threaded into the AGB +
  // soil-carbon AuthTileLayers as their `bounds` option so Leaflet only
  // fetches tile coords that intersect the shed. The pane's clip-path
  // already masks anything outside the shed visually; the wasted decode +
  // network for those out-of-bounds tiles is what made cold AGB activation
  // feel slow (typically ~75% of viewport tiles are outside the shed at
  // the post-flyToBounds zoom level). For the demo's 20 fixed sheds this
  // is a stable per-shed value — recomputes only when the user toggles
  // 60-min ↔ 90-min or jumps mills, never on year scrubs.
  const shedBounds = useMemo<L.LatLngBounds | undefined>(
    () => (boundary ? L.geoJSON(boundary as never).getBounds() : undefined),
    [boundary],
  );

  // Swap the basemap inside the shed to Stadia satellite when a thematic
  // basemap (AGB gist_earth ramp etc.) is showing under a translucent
  // color-ramped data overlay — the two ramps otherwise mix and distort the
  // overlay's apparent colors. Outside the shed stays on the thematic
  // basemap so the user still sees its context. Driven by *any* active
  // layer (not just the primary) because scenario steps can stack a
  // raster overlay (e.g. canopy_height) under a vector overlay (e.g.
  // tree_crown_segmentation): the primary in that case is the vector,
  // but the swap rationale still applies.
  const hasActiveRasterOverlay = activeLayers.some((id) =>
    LAYERS_WITH_RASTER_OVERLAY.has(id),
  );
  const swapInShedBasemap =
    basemap.kind === 'thematic' && boundary !== null && hasActiveRasterOverlay;
  const stadiaBasemap = BASEMAPS.stadia_satellite;
  // Lift the basemap's maxZoom cap when the user needs to zoom past the
  // thematic basemap's native ceiling: either because a raster overlay
  // is being inspected (handled by swapInShedBasemap), or because the
  // per-crown native view is on and its cinematic fly targets z≈16-18.
  // Without this, individual_tree step 3 on the AGB basemap (maxZoom=10)
  // gets clamped back to z=10 mid-fly and the audience never sees the
  // crown polygons.
  const suppressBasemapMax = swapInShedBasemap || nativeViewActive;

  // fillOpacity 0.13 is calibrated for lime (#c1f04f) over the dark satellite
  // basemap — the prior ice-blue stroke read at 0.05, but lime is brighter and
  // needs less alpha to land at the same perceptual weight. Tuned by eye, not
  // a 1:1 token swap.
  const supplyShedStyle = {
    color: '#c1f04f',
    weight: 2.5,
    opacity: 0.8,
    dashArray: '6 4',
    fillOpacity: 0.13,
    fillColor: '#c1f04f',
  };

  return (
    <div className="relative h-full w-full">
      {/* Floating DEMO chip on the map (desktop only). The panel header
          already carries a DemoBadge, but a user who scrolls deep into
          a layer panel — or collapses the panel via the toggle tab —
          loses sight of it. The map chip keeps the "this is a demo
          prototype" signal persistently visible. Mobile is hidden
          because the bottom-sheet peek bar already renders the
          DemoBadge as part of the persistent chrome. */}
      <div
        className="hidden md:block absolute z-[450] left-4"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
      >
        <DemoBadge />
      </div>

      {/* Mobile-only floating scenario card. Renders at the top of the
          map so a user in map mode (bottom sheet collapsed) keeps the
          step narrative + step indicator in view. Hidden on desktop
          (overlay is `md:hidden`); free exploration leaves
          activeScenario null and the overlay short-circuits. */}
      {activeScenario && scenarioStep && (
        <MobileScenarioMapOverlay
          scenario={activeScenario}
          step={scenarioStep}
          stepIndex={scenarioStepIndex}
          totalSteps={scenarioTotalSteps}
          isFirstStep={scenarioIsFirstStep}
          isLastStep={scenarioIsLastStep}
          onPrev={onScenarioPrev}
          onNext={onScenarioNext}
          onOpenPanel={onOpenMobileSheet}
          onOpenInsight={onOpenMobileSheetToInsight}
        />
      )}

      <MapContainer
        center={[mill.latitude, mill.longitude]}
        zoom={8}
        className="h-full w-full"
        zoomControl={false}
        scrollWheelZoom={true}
      >
        {/* Base map layer — key forces remount on basemap switch */}
        <TileLayer
          key={basemap.id}
          url={basemap.url}
          attribution={basemap.attribution}
          opacity={basemap.opacity ?? 1}
          minZoom={basemap.minZoom}
          maxZoom={basemap.maxZoom ?? 18}
          maxNativeZoom={basemap.maxNativeZoom}
          bounds={basemap.kind === 'thematic' ? MAP_CONFIG.thematicBasemapBounds : undefined}
        />
        <BasemapZoomClamp
          basemap={basemap}
          fallbackMinZoom={MAP_CONFIG.minZoom}
          fallbackMaxZoom={18}
          // No data layer hard-caps user zoom anymore. AGB and Soil
          // Carbon both expose `maxNativeZoom` to their TileLayers (so
          // Leaflet upscales the top-tier tile past the pyramid's
          // ceiling instead of 404-ing) and let the user keep zooming
          // for any deeper layer stacked on top.
          layerMaxZoom={null}
          suppressBasemapMax={suppressBasemapMax}
        />

        {/* In-shed basemap swap — paints Stadia satellite inside the shed
            (pane z=250, above the thematic basemap at z=200, below the data
            tile pane at z=300). Only active when a raster-overlay data layer
            is selected on a thematic basemap, so the overlay's color ramp
            reads against a neutral satellite backdrop instead of blending
            with the thematic basemap's own ramp. */}
        {swapInShedBasemap && boundary && (
          <SupplyShedClipPane
            boundary={boundary}
            paneName="in-shed-basemap-pane"
            zIndex={250}
          >
            <TileLayer
              key="in-shed-basemap"
              url={stadiaBasemap.url}
              attribution={stadiaBasemap.attribution}
            />
          </SupplyShedClipPane>
        )}

        {/* Spotlight mask — dims the basemap everywhere except inside the
            supply shed. Only shown for thematic basemaps (AGB and future
            color-ramp rasters); satellite basemaps render normally. */}
        {showSpotlight && boundary && <SpotlightMask polygon={boundary} />}

        {/* Inland water mask — paints Great Lakes etc. solid dark on
            thematic basemaps whose source raster lacks a proper water
            mask. Sits above the spotlight mask (replaces the dimmed
            basemap over lakes with a clean black) and below data raster
            overlays (so data still paints on top if it extends over
            water, which is rare). */}
        {basemap.kind === 'thematic' && <WaterMask />}

        {/* Precomputed PNG overlays. Opacity is high (0.9) for WHP because its
            RdYlGn ramp blends into green satellite imagery at lower opacity
            (pale yellow "moderate" reads as "low"). Species key includes the
            variant so React-Leaflet remounts on dominant ↔ richness swap. */}
        <PngRasterOverlay
          active={activeLayers.includes('fire_risk')}
          overlay={whpOverlay}
          keyBase={`whp-${mill.id}-${selectedDriveTime}`}
          opacity={0.9}
        />
        <PngRasterOverlay
          active={activeLayers.includes('species_diversity')}
          overlay={speciesOverlay}
          keyBase={`species-${mill.id}-${selectedDriveTime}-${speciesOverlayVariant}`}
          opacity={0.85}
        />
        <PngRasterOverlay
          active={activeLayers.includes('stand_density')}
          overlay={sdOverlay}
          keyBase={`sd-${mill.id}-${selectedDriveTime}`}
          opacity={0.85}
        />
        <PngRasterOverlay
          active={activeLayers.includes('forest_ownership')}
          overlay={foOverlay}
          keyBase={`fo-${mill.id}-${selectedDriveTime}`}
          opacity={0.85}
        />
        {/* Private-timberland focus mask — sibling PNG that paints
            translucent black over non-private classes so family + corporate
            stay vibrant on top of the categorical Forest Ownership overlay
            during scenario steps. Same overlayPane as the FO PNG; an
            explicit zIndex forces deterministic paint order so the mask
            always sits on top regardless of mount sequence. Mask alpha is
            baked into the PNG (transparent over family / corporate, ~50%
            black elsewhere). Hidden in free exploration. */}
        <PngRasterOverlay
          active={foPrivateFocus && activeLayers.includes('forest_ownership')}
          overlay={foPrivateMaskOverlay}
          keyBase={`fo-mask-${mill.id}-${selectedDriveTime}`}
          opacity={1}
          zIndex={10}
        />
        <PngRasterOverlay
          active={activeLayers.includes('imperiled_species')}
          overlay={imperiledSpeciesOverlay}
          keyBase={`mobi-${mill.id}-${selectedDriveTime}`}
          opacity={0.85}
        />
        <PngRasterOverlay
          active={activeLayers.includes('resilience_priority')}
          overlay={resiliencePriorityOverlay}
          keyBase={`resilience-priority-${mill.id}-${selectedDriveTime}`}
          opacity={0.95}
        />
        <PerCrownNativeOverlay
          active={nativeViewActive && activeLayers.includes('tree_crown_segmentation')}
          window={perCrownNative.data}
        />


        {/* Canopy height — Earthscale-hosted XYZ tile pyramid rather than a
            precomputed supply-shed PNG. Rendered into a dedicated pane that
            is CSS clip-path'd to the supply shed polygon, so tiles only paint
            inside the shed (matching the feel of the precomputed overlays).
            One TileLayer per state dataset the shed overlaps — cross-border
            sheds (e.g. Alabama Piedmont into Georgia) stack multiple datasets
            so tiles stitch across state lines. A shared rescale keeps the
            viridis ramp continuous at the seam. */}
        {activeLayers.includes('canopy_height') && canopyHeight.isAvailable && boundary && (
          <SupplyShedClipPane boundary={boundary}>
            {canopyHeight.layers.map((layer) => (
              <TileLayer
                key={`canopy-height-${layer.state}`}
                url={layer.tileUrl}
                bounds={layer.bounds}
                opacity={0.9}
                // Cap tile fetches at the source's native max so Leaflet
                // upsamples past it instead of 404-ing on tiles that don't
                // exist in the Earthscale pyramid. Without this prop the
                // layer blanks past z=15 — both in free mode (now that the
                // canopy zoom clamp is gone) and during the per-crown CTA
                // fly in scenario mode, which lands at z=16-18.
                maxNativeZoom={canopyHeight.maxZoom ?? undefined}
              />
            ))}
          </SupplyShedClipPane>
        )}

        {/* AGB year-slider overlay — precomputed S3 tile pyramid, one tier
            per year (scripts/scrape-agb-overlay-tiles.py). Rendered inside
            AgbCrossfadeOverlay, which stacks two layers during year swaps
            (old year underneath, new year fading in on top) so the shed
            is never blank between scrub and tile-paint. Clipped to the
            shed so the 100 m dataset paints only where the user cares.
            Suppressed in change mode so we don't paint two AGB renderings
            on top of each other. */}
        <AgbCrossfadeOverlay
          pendingUrl={agbOverlay.tileUrl}
          boundary={boundary}
          bounds={shedBounds}
          enabled={activeLayers.includes('aboveground_biomass') && !agbChangeMode}
          onLoadingChange={handleAgbLoadingChange}
          maxNativeZoom={agbOverlay.maxNativeZoom}
        />

        {/* AGB change overlay — precomputed PNG of the smoothed-endpoint
            difference (mean 2021-25 minus mean 2000-04). 5-bin diverging
            palette, clipped to the shed offline. Painted instead of the
            year-tile crossfade when a scenario step requests it. */}
        <PngRasterOverlay
          active={agbChangeMode && activeLayers.includes('aboveground_biomass')}
          overlay={agbChangeOverlay}
          keyBase={`agb-change-${mill.id}-${selectedDriveTime}`}
          opacity={0.95}
        />

        {/* Soil carbon dynamic overlay — Earthmover Flux serves a pair of
            per-depth rasters (0–30 cm, 0–100 cm) from the same global 100 m
            catalog as AGB. No temporal axis here, so we skip the crossfade
            and render a single AuthTileLayer; swapping `soilCarbonDepth`
            points at the sibling dataset with its own colorscalerange, which
            re-resolves the URL and triggers a fresh fetch. Clipped to the
            shed polygon because the raw dataset is global. */}
        {activeLayers.includes('soil_carbon')
          && soilCarbonOverlay.isAvailable
          && soilCarbonOverlay.tileUrl
          && soilCarbonOverlay.authHeader
          && boundary && (
          <SupplyShedClipPane boundary={boundary} paneName="soc-pane" zIndex={300}>
            <AuthTileLayer
              url={soilCarbonOverlay.tileUrl}
              authHeader={soilCarbonOverlay.authHeader}
              opacity={0.9}
              maxNativeZoom={soilCarbonOverlay.maxNativeZoom}
              bounds={shedBounds}
              onAuthError={handleSoilCarbonAuthError}
            />
          </SupplyShedClipPane>
        )}

        {/* Supply shed boundary (drive-time isochrone + forest road buffer).
            `interactive: false` because the boundary is a single polygon
            whose tinted fill covers the whole shed — leaving it interactive
            would intercept mouseover/mousemove events meant for the
            canvas-rendered crown polygons underneath (and the SVG renderer
            is added to the same overlayPane DOM after the canvas, so a
            filled interactive path would shadow the canvas hit-testing). */}
        {boundary && (
          <GeoJSON
            key={boundaryKey}
            data={boundary}
            style={supplyShedStyle}
            interactive={false}
          />
        )}

        {/* Map view controller — fits to supply shed boundary, or flies to
            scenario-declared focus when a scenario step overrides. Also
            handles the rising-edge fly into the per-crown native window
            when the left-panel button toggles the overlay on. */}
        <MapViewController
          mill={mill}
          boundary={boundary}
          scenarioMapFocus={scenarioMapFocus}
          nativeFlyTarget={nativeFlyTarget}
        />

        {/* Map controls */}
        <MapZoomControl>
          <BasemapSwitchControl onClick={openPicker} />
          {/* Hide the compact layer dropdown on mobile — the same listbox
              lives in the bottom-sheet panel, and the trigger's "ABOVEGROUND
              BIOMASS"-class labels overflow next to the zoom controls in a
              375 px viewport. md+ keeps the corner control. */}
          <div className="hidden md:block">
            <DetailLayerDropdown
              selectedLayer={selectedLayer}
              onLayerChange={onLayerChange}
              millCode={mill.metadata?.code ?? null}
              driveTime={selectedDriveTime}
            />
          </div>
        </MapZoomControl>
      </MapContainer>

      {/* Loading overlay while the supply shed resolves */}
      {supplyShed.isLoading && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[500]">
          <div className="w-16 h-16 border-4 border-figma-yellow/20 border-t-figma-yellow rounded-full animate-spin" />
        </div>
      )}

      {/* Error overlay when supply shed fails to load */}
      {!supplyShed.isLoading && supplyShed.error && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[500] bg-figma-card/90 border border-red-500/50 rounded-lg px-4 py-2">
          <p className="text-sm font-roboto text-red-400">{supplyShed.error}</p>
        </div>
      )}

      {/* Auth-failure banner for the soil-carbon dynamic tile layer. The
          AuthTileLayer surfaces 401/403 here instead of letting Leaflet
          paint a wall of broken-tile icons; the banner gives the user
          (and ops) an explicit signal that the Earthmover Flux token is
          stale or revoked. Cleared automatically when the user toggles
          the layer off, or when the auth header changes (the underlying
          layer rebuilds and resets its sticky-error flag). */}
      {soilCarbonAuthError !== null && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[500] bg-figma-card/95 border border-red-500/60 rounded-lg px-4 py-2 max-w-md shadow-figma-card">
          <p className="text-sm font-roboto-mono text-red-400 uppercase tracking-wider">
            Soil carbon · auth failed (HTTP {soilCarbonAuthError})
          </p>
          <p className="mt-1 text-xs font-roboto text-neutral-300">
            The Earthmover Flux token is missing or expired. Refresh
            <code className="px-1 mx-1 bg-figma-panel rounded text-figma-lime">VITE_EARTHMOVER_TOKEN</code>
            and reload, or hide this layer to continue.
          </p>
        </div>
      )}

      {/* Native-resolution view banner. Shows whenever the per-crown native
          overlay is active, with a close affordance that flips the toggle
          off and (per the falling-edge handler in DetailMapView) flies the
          camera back to the active scenario step's mapFocus, or to entry-
          zoom-2 in free mode. Anchored bottom-center because the top-center
          slot collides with the layer dropdown / basemap switch / zoom
          controls clustered in the top-right at common laptop widths.
          The bottom-center slot is empty in Sierra Nevada per-tree mode
          (AgbTimeSlider only mounts when `selectedLayer === 'aboveground_
          biomass'`, which isn't true here), and Leaflet's attribution
          stays bottom-right, so this banner has the band to itself. */}
      {nativeViewActive && perCrownNative.data && (
        <div className="absolute bottom-[var(--mobile-peek-clearance)] left-4 right-4 md:left-1/2 md:right-auto md:-translate-x-1/2 z-[500] flex items-center gap-4 bg-figma-card/95 border border-figma-yellow/60 rounded-lg px-4 py-2.5 shadow-figma-card backdrop-blur-sm">
          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
            <span className="text-xs font-roboto-mono text-figma-yellow uppercase tracking-wider truncate">
              {perCrownNative.data.contextBoundary?.name ?? 'Native resolution'}
            </span>
            <span className="text-xs font-roboto text-neutral-300 truncate">
              {perCrownNative.data.crownCount.toLocaleString()} trees ·{' '}
              <span className="md:hidden">pinch to zoom, tap a tree for details</span>
              <span className="hidden md:inline">hover for details</span>
            </span>
          </div>
          <button
            type="button"
            onClick={onExitNativeView}
            aria-label="Exit native resolution view"
            className="flex-shrink-0 text-neutral-400 hover:text-figma-yellow transition-colors text-base leading-none"
          >
            ✕
          </button>
        </div>
      )}

      {/* Categorical legend for the WHP overlay — colors match the
          left-panel bar chart (same national 5-class classification). */}
      {selectedLayer === 'fire_risk' && whpOverlay.url && <WhpMapLegend />}

      {/* Species overlay toggle + legend stacked in the bottom-left corner.
          Grouping them in one absolutely-positioned container means the
          toggle always sits cleanly above the legend without depending on
          the legend's (variable) height. Lifted above the mobile bottom-
          sheet peek bar via `--mobile-peek-clearance` (md+ resets to 16 px). */}
      {selectedLayer === 'species_diversity' && (
        <div className="absolute bottom-[var(--mobile-peek-clearance)] left-4 z-[500] flex flex-col gap-2 items-start max-w-[calc(100%-32px)]">
          <SpeciesOverlayToggle
            variant={speciesOverlayVariant}
            onChange={onSpeciesOverlayVariantChange}
          />
          {speciesOverlay.url && speciesOverlay.meta && (
            <SpeciesMapLegend meta={speciesOverlay.meta} />
          )}
        </div>
      )}

      {/* Categorical legend for the SD overlay — same 4-bin RdYlGn palette
          the left-panel bar chart uses. */}
      {selectedLayer === 'stand_density' && sdOverlay.url && <SdMapLegend />}

      {/* Categorical legend for the Forest Ownership overlay — colors match
          the left-panel pie chart. When the private-timberland mask is on,
          dim non-private chips so the legend matches the map's emphasis.
          Hidden in scenario steps where AGB-change mode is on (Carbon
          Offense steps 2 + 3): the diverging red/green delta is the
          visually dominant overlay and its legend takes over. Without
          this guard the legend showed FO classes while the user was
          looking at AGB-change pixels — the wrong key. */}
      {selectedLayer === 'forest_ownership' && foOverlay.url && !agbChangeMode && (
        <FoMapLegend privateFocus={foPrivateFocus} />
      )}

      {/* Categorical legend for the MoBI (Imperiled Wildlife Species) overlay —
          plasma 5-class ramp derived from national percentile cuts, matching
          the left-panel bar chart. */}
      {selectedLayer === 'imperiled_species' && imperiledSpeciesOverlay.url && (
        <ImperiledSpeciesMapLegend />
      )}

      {/* Resilience Priority — synthesis layer used by Carbon Defense
          insight step. Single-color highlight; legend restates the criteria
          so a free-exploration viewer who selects this from the dropdown
          can read it without context. */}
      {selectedLayer === 'resilience_priority' && resiliencePriorityOverlay.url && (
        <ResiliencePriorityMapLegend />
      )}


      {/* Viridis gradient legend for the canopy-height tile layer. Values are
          native meters (server-side rescale drives the colormap). */}
      {selectedLayer === 'canopy_height' && canopyHeight.isAvailable && canopyHeight.rescale && (
        <CanopyHeightMapLegend rescale={canopyHeight.rescale} />
      )}

      {/* Gist-earth gradient legend for the AGB basemap. Visible when the
          AGB basemap is painting the map AND no raster overlay layer is on
          top (which would have its own legend / slider stack). Rescale
          [0, 500] Mg/ha matches the vmin/vmax the precompute script passes
          to matplotlib `gist_earth`. Hidden on mobile while the per-crown
          native-view banner is up — both anchor bottom-left and a 375 px
          viewport can't fit them side-by-side without visual collision.
          Desktop is unaffected: the banner there is centered, legend stays
          in the corner. */}
      {basemap.id === 'earthmover_agb_100m' &&
        !(selectedLayer && LAYERS_WITH_RASTER_OVERLAY.has(selectedLayer)) && (
          <div
            className={`absolute bottom-[var(--mobile-peek-clearance)] left-4 z-[500] ${
              nativeViewActive ? 'hidden md:block' : ''
            }`}
          >
            <AgbMapLegend rescale={[0, 500]} />
          </div>
        )}

      {/* AGB overlay controls — slider on top, legend below — grouped into a
          single bottom-left column so they never collide at narrow viewport
          widths. Mirrors the species overlay stacking pattern. In change
          mode the year slider is meaningless (the overlay is a single
          baseline-vs-latest delta), so we hide it and show the diverging
          change legend instead. */}
      {selectedLayer === 'aboveground_biomass' && !agbChangeMode && (
        <div className="absolute bottom-[var(--mobile-peek-clearance)] left-4 right-4 md:right-auto z-[500] flex flex-col gap-2 items-stretch md:items-start">
          <AgbTimeSlider
            year={agbYear}
            minYear={agbOverlay.minYear}
            maxYear={agbOverlay.maxYear}
            onChange={onAgbYearChange}
            isLoading={isAgbOverlayLoading}
          />
          {/* Legend is desktop-only — on a phone the slider + scenario
              card + DETAILS pill already dominate the bottom edge, and
              the AGB color ramp is itself perceptually intuitive
              (dark → bright = low → high biomass). Free-mode users can
              tap "Details" to reach the per-shed AGB stats panel for
              the precise rescale numbers. */}
          <div className="hidden md:block">
            <AgbMapLegend rescale={agbOverlay.rescale} />
          </div>
        </div>
      )}
      {/* AGB-change diverging legend. Drops the `selectedLayer ===
          'aboveground_biomass'` requirement on purpose: scenario steps
          like Carbon Offense step 2 list AGB and Forest Ownership in
          activeLayers and resolve `forest_ownership` as the primary
          (last) layer, but the visually dominant overlay is the AGB
          change diff — so the legend should follow `agbChangeMode`,
          not the primary-layer key. */}
      {agbChangeMode && agbChangeOverlay.url && <AgbChangeMapLegend />}

      {/* Gist-earth gradient legend for the soil-carbon overlay. The rescale
          tracks the active depth (0–30 cm uses 0–250 Mg C/ha, 0–100 cm uses
          0–650) so the legend matches what Flux is actually rendering. */}
      {selectedLayer === 'soil_carbon' && soilCarbonOverlay.isAvailable && (
        <div className="absolute bottom-[var(--mobile-peek-clearance)] left-4 z-[500]">
          <SoilCarbonMapLegend rescale={soilCarbonOverlay.rescale} depth={soilCarbonDepth} />
        </div>
      )}
    </div>
  );
};
